import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Modal, Popconfirm, Table, toast } from '../../../ui';
import {
  DELIVERABLE_FILE_TYPES,
  REPOSITORY_FILE_TYPE,
  deleteDeliverableByProduct,
  getAllDeliverables,
  getProductOptions,
  parseGitHubRepository,
  saveDeliverable,
  validateDeliverableData,
} from '../../../services/deliverableService';
import { formatDateTime } from '../../../utils/format';
import { AdminHeader, LoadError, lookup } from './orderAdmin';

const BYTES_PER_MB = 1024 * 1024;

const sizeToMb = bytes => {
  const value = Number(bytes);
  return value > 0 ? String(Math.round((value / BYTES_PER_MB) * 100) / 100) : '';
};

const DeliverableManagement = () => {
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the deliverable being edited, or null when adding
  const [form, setForm] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const nextKey = useRef(0);

  const newItem = useCallback(
    (values = {}) => ({
      key: `item-${(nextKey.current += 1)}`,
      fileType: REPOSITORY_FILE_TYPE,
      title: '',
      url: '',
      description: '',
      sizeMb: '',
      ...values,
    }),
    []
  );

  const fetchDeliverables = useCallback(async () => {
    setLoading(true);
    const result = await getAllDeliverables();
    if (result.success) {
      setDeliverables(result.data);
      setError('');
    } else {
      setError(
        result.status === 404 || result.status === 405
          ? 'The server does not have the deliverables list yet. It appears after the API is updated.'
          : result.error
      );
    }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async () => {
    const result = await getProductOptions();
    if (result.success) {
      setProducts(result.data);
      setProductsError('');
    } else {
      setProductsError(result.error);
    }
  }, []);

  useEffect(() => {
    fetchDeliverables();
    fetchProducts();
  }, [fetchDeliverables, fetchProducts]);

  const productsById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);
  const deliverablesByProduct = useMemo(
    () => Object.fromEntries(deliverables.map(d => [d.productId, d])),
    [deliverables]
  );

  const openAdd = () => {
    setEditing(null);
    setForm({
      productId: '',
      edition: 'Standard',
      items: [newItem()],
      accessInstructions: '',
      licenseTerms: '',
      isActive: true,
    });
    setFormErrors([]);
    setEditorOpen(true);
  };

  const openEdit = deliverable => {
    setEditing(deliverable);
    setForm({
      productId: deliverable.productId,
      edition: deliverable.edition || 'Standard',
      items: (deliverable.deliverableContent?.length ? deliverable.deliverableContent : [{}]).map(item =>
        newItem({
          fileType: item.fileType || 'other',
          title: item.title || '',
          url: item.url || '',
          description: item.description || '',
          mimeType: item.mimeType || '',
          sizeMb: sizeToMb(item.fileSizeInBytes),
          createdAt: item.createdAt,
          accessDuration: item.accessDuration,
        })
      ),
      accessInstructions: deliverable.accessInstructions || '',
      licenseTerms: deliverable.licenseTerms || '',
      isActive: deliverable.isActive !== false,
    });
    setFormErrors([]);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (!saving) setEditorOpen(false);
  };

  const setField = (name, value) => setForm(current => ({ ...current, [name]: value }));

  const setItem = (key, name, value) =>
    setForm(current => ({
      ...current,
      items: current.items.map(item => (item.key === key ? { ...item, [name]: value } : item)),
    }));

  const removeItem = key =>
    setForm(current => ({ ...current, items: current.items.filter(item => item.key !== key) }));

  const addItem = () => setForm(current => ({ ...current, items: [...current.items, newItem({ fileType: 'zip' })] }));

  const handleSave = async event => {
    event.preventDefault();
    const payload = {
      ...form,
      deliverableContent: form.items.map(item => ({
        ...item,
        fileSizeInBytes: item.sizeMb ? Math.round(parseFloat(item.sizeMb) * BYTES_PER_MB) || 0 : 0,
      })),
    };
    const validation = validateDeliverableData(payload);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    setSaving(true);
    setFormErrors([]);
    const result = await saveDeliverable(payload);
    setSaving(false);
    if (result.success) {
      toast.success(result.message);
      setEditorOpen(false);
      fetchDeliverables();
    } else {
      setFormErrors([result.error]);
    }
  };

  const handleDelete = async deliverable => {
    const result = await deleteDeliverableByProduct(deliverable.productId);
    if (result.success) {
      toast.success('Deliverables deleted');
      fetchDeliverables();
    } else {
      toast.error(result.error);
    }
  };

  const productName = deliverable =>
    lookup(productsById, deliverable.productId)?.name || deliverable.productName || 'Unknown product';

  const columns = [
    {
      title: 'Product',
      key: 'product',
      width: 260,
      render: (_, record) => (
        <div>
          <div className="oa-strong">{productName(record)}</div>
          <span className="oa-sub oa-mono">{lookup(productsById, record.productId)?.code || record.productId}</span>
        </div>
      ),
    },
    {
      title: 'Edition',
      dataIndex: 'edition',
      key: 'edition',
      width: 120,
    },
    {
      title: 'Items',
      key: 'items',
      width: 200,
      render: (_, record) => {
        const content = record.deliverableContent || [];
        const repos = content.filter(c => c.fileType === REPOSITORY_FILE_TYPE).length;
        return (
          <div className="oa-inline">
            <span>
              {content.length} {content.length === 1 ? 'item' : 'items'}
            </span>
            {repos > 0 && (
              <span className="pk-badge" data-tone="info">
                GitHub access
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      render: isActive => (
        <span className="pk-badge" data-tone={isActive ? 'success' : undefined}>
          {isActive ? 'Active' : 'Off'}
        </span>
      ),
    },
    {
      title: 'Updated',
      key: 'updated',
      width: 170,
      render: (_, record) => <span className="oa-small">{formatDateTime(record.updatedAt || record.createdAt)}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      render: (_, record) => (
        <div className="oa-inline">
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--xs"
            onClick={() => openEdit(record)}
            aria-label={`Edit deliverables of ${productName(record)}`}
          >
            <Pencil aria-hidden="true" /> Edit
          </button>
          <Popconfirm
            title={`Delete the deliverables of ${productName(record)}?`}
            description="Orders keep what was already delivered. New orders of this product get nothing until you add deliverables again."
            okText="Delete"
            okDanger
            onConfirm={() => handleDelete(record)}
          >
            <button
              type="button"
              className="pk-btn pk-btn--danger pk-btn--xs"
              aria-label={`Delete deliverables of ${productName(record)}`}
            >
              <Trash2 aria-hidden="true" /> Delete
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const selectedHasDeliverable = form && !editing && lookup(deliverablesByProduct, form.productId);

  return (
    <div className="oa-page">
      <AdminHeader
        title="Deliverables"
        sub="What a buyer receives for each product. Each product has one set of deliverables; saving again replaces it."
        actions={
          <>
            <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={fetchDeliverables} disabled={loading}>
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
            <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openAdd}>
              <Plus aria-hidden="true" /> Add deliverables
            </button>
          </>
        }
      />

      <div className="pk-alert" data-tone="info" role="note">
        <Info aria-hidden="true" />
        <div>
          Items of type <strong>GitHub repository</strong> add the buyer's GitHub account as a collaborator after
          payment. Other items are links the buyer opens from their order page. Paid orders that were waiting for a
          product's deliverables are retried automatically after you save.
        </div>
      </div>

      {error ? (
        <LoadError title="Couldn't load deliverables" message={error} onRetry={fetchDeliverables} />
      ) : (
        <div className="pk-card oa-panel oa-panel--flush">
          <div className="oa-panel__head">
            <h2 className="oa-panel__title">Products with deliverables</h2>
            <span className="oa-small oa-muted" aria-live="polite">
              {loading ? 'Loading…' : `${deliverables.length} ${deliverables.length === 1 ? 'product' : 'products'}`}
            </span>
          </div>
          <Table
            columns={columns}
            dataSource={deliverables}
            loading={loading}
            rowKey="productId"
            scroll={{ x: 1000 }}
            emptyText="No deliverables yet. Add them so paid orders can be delivered."
            pagination={{ pageSize: 20, hideOnSinglePage: true, showTotal: total => `${total} products` }}
          />
        </div>
      )}

      <Modal
        open={editorOpen}
        onClose={closeEditor}
        title={editing ? `Edit deliverables: ${productName(editing)}` : 'Add deliverables'}
        footer={null}
        width={760}
      >
        {form && (
          <form className="oa-dialog" onSubmit={handleSave} noValidate>
            <div className="oa-grid-2">
              <div className="pk-field">
                <label className="pk-label" htmlFor="deliverable-product">
                  Product
                </label>
                {editing ? (
                  <input id="deliverable-product" className="pk-input oa-sm" value={productName(editing)} disabled />
                ) : (
                  <select
                    id="deliverable-product"
                    className="pk-select oa-sm"
                    value={form.productId}
                    onChange={e => setField('productId', e.target.value)}
                    required
                  >
                    <option value="">Choose a product</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                        {product.code ? ` (${product.code})` : ''}
                        {product.isActive ? '' : ' · inactive'}
                        {lookup(deliverablesByProduct, product.id) ? ' · has deliverables' : ''}
                      </option>
                    ))}
                  </select>
                )}
                {productsError && !editing && (
                  <span className="pk-error">
                    {productsError}{' '}
                    <button type="button" className="pk-btn pk-btn--ghost pk-btn--xs" onClick={fetchProducts}>
                      Try again
                    </button>
                  </span>
                )}
                {selectedHasDeliverable && (
                  <span className="pk-hint">This product already has deliverables. Saving replaces them.</span>
                )}
              </div>
              <div className="pk-field">
                <label className="pk-label" htmlFor="deliverable-edition">
                  Edition
                </label>
                <input
                  id="deliverable-edition"
                  className="pk-input oa-sm"
                  value={form.edition}
                  maxLength={60}
                  onChange={e => setField('edition', e.target.value)}
                  placeholder="Standard"
                />
              </div>
            </div>

            <h3 className="oa-section-title">Items</h3>
            {form.items.map((item, index) => {
              const repo = item.fileType === REPOSITORY_FILE_TYPE ? parseGitHubRepository(item.url) : null;
              const idBase = `deliverable-item-${item.key}`;
              return (
                <fieldset key={item.key} className="oa-item-editor">
                  <legend className="sr-only">Item {index + 1}</legend>
                  <div className="oa-item-editor__head">
                    <span>Item {index + 1}</span>
                    {form.items.length > 1 && (
                      <button
                        type="button"
                        className="pk-btn pk-btn--ghost pk-btn--xs"
                        onClick={() => removeItem(item.key)}
                        aria-label={`Remove item ${index + 1}`}
                      >
                        <Trash2 aria-hidden="true" /> Remove
                      </button>
                    )}
                  </div>
                  <div className="oa-grid-2">
                    <div className="pk-field">
                      <label className="pk-label" htmlFor={`${idBase}-type`}>
                        Type
                      </label>
                      <select
                        id={`${idBase}-type`}
                        className="pk-select oa-sm"
                        value={item.fileType}
                        onChange={e => setItem(item.key, 'fileType', e.target.value)}
                      >
                        {DELIVERABLE_FILE_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                        {!DELIVERABLE_FILE_TYPES.some(type => type.value === item.fileType) && item.fileType && (
                          <option value={item.fileType}>{item.fileType}</option>
                        )}
                      </select>
                    </div>
                    <div className="pk-field">
                      <label className="pk-label" htmlFor={`${idBase}-title`}>
                        Title
                      </label>
                      <input
                        id={`${idBase}-title`}
                        className="pk-input oa-sm"
                        value={item.title}
                        maxLength={120}
                        onChange={e => setItem(item.key, 'title', e.target.value)}
                        placeholder={item.fileType === REPOSITORY_FILE_TYPE ? 'Source code repository' : 'Project files'}
                      />
                    </div>
                  </div>
                  <div className="pk-field">
                    <label className="pk-label" htmlFor={`${idBase}-url`}>
                      {item.fileType === REPOSITORY_FILE_TYPE ? 'Repository link' : 'Link'}
                    </label>
                    <input
                      id={`${idBase}-url`}
                      type="url"
                      inputMode="url"
                      className="pk-input oa-sm"
                      value={item.url}
                      onChange={e => setItem(item.key, 'url', e.target.value)}
                      placeholder={
                        item.fileType === REPOSITORY_FILE_TYPE
                          ? 'https://github.com/owner/repository'
                          : 'https://drive.google.com/…'
                      }
                      aria-describedby={`${idBase}-url-hint`}
                    />
                    <span className="pk-hint" id={`${idBase}-url-hint`}>
                      {item.fileType === REPOSITORY_FILE_TYPE
                        ? repo
                          ? `Buyers are invited to ${repo.owner}/${repo.repo}. The GitHub token must have admin access to it.`
                          : 'The buyer is invited as a collaborator to this repository after payment.'
                        : 'Buyers open this link from their order page; it is never shown before payment.'}
                    </span>
                  </div>
                  <div className="oa-grid-2">
                    <div className="pk-field">
                      <label className="pk-label" htmlFor={`${idBase}-description`}>
                        Description <span className="pk-optional">(optional)</span>
                      </label>
                      <input
                        id={`${idBase}-description`}
                        className="pk-input oa-sm"
                        value={item.description}
                        maxLength={300}
                        onChange={e => setItem(item.key, 'description', e.target.value)}
                      />
                    </div>
                    {item.fileType !== REPOSITORY_FILE_TYPE && (
                      <div className="pk-field">
                        <label className="pk-label" htmlFor={`${idBase}-size`}>
                          Size in MB <span className="pk-optional">(optional)</span>
                        </label>
                        <input
                          id={`${idBase}-size`}
                          type="number"
                          min={0}
                          step="0.01"
                          className="pk-input oa-sm"
                          value={item.sizeMb}
                          onChange={e => setItem(item.key, 'sizeMb', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </fieldset>
              );
            })}
            <div>
              <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={addItem}>
                <Plus aria-hidden="true" /> Add item
              </button>
            </div>

            <div className="pk-field">
              <label className="pk-label" htmlFor="deliverable-instructions">
                Access instructions <span className="pk-optional">(optional)</span>
              </label>
              <textarea
                id="deliverable-instructions"
                className="pk-textarea oa-sm"
                rows={3}
                maxLength={2000}
                value={form.accessInstructions}
                onChange={e => setField('accessInstructions', e.target.value)}
              />
            </div>

            <div className="pk-field">
              <label className="pk-label" htmlFor="deliverable-licence">
                Licence terms <span className="pk-optional">(optional)</span>
              </label>
              <textarea
                id="deliverable-licence"
                className="pk-textarea oa-sm"
                rows={3}
                maxLength={4000}
                value={form.licenseTerms}
                onChange={e => setField('licenseTerms', e.target.value)}
              />
            </div>

            <label className="pk-check" htmlFor="deliverable-active">
              <input
                id="deliverable-active"
                type="checkbox"
                checked={form.isActive}
                onChange={e => setField('isActive', e.target.checked)}
              />
              <span>Active. Inactive deliverables are not delivered to new orders.</span>
            </label>

            {formErrors.length > 0 && (
              <div className="pk-alert" data-tone="danger" role="alert">
                <AlertTriangle aria-hidden="true" />
                <div>
                  {formErrors.map(message => (
                    <div key={message}>{message}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="oa-dialog-actions">
              <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={closeEditor} disabled={saving}>
                Cancel
              </button>
              <button
                type="submit"
                className="pk-btn pk-btn--primary pk-btn--sm"
                disabled={saving}
                aria-busy={saving || undefined}
              >
                Save
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default DeliverableManagement;
